import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { userDataService } from 'src/app/services/userDataService';
import { userServices } from 'src/app/services/users.service';
import { ManageFollowersModalComponent } from './add-followers/manage-followers';
import { DialogComponent } from '../dialog/confirm.dialog';

@Component({
  selector: 'app-followers-button',
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatButtonModule, MatIcon],
  templateUrl: './followers-button.component.html',
  styleUrl: './followers-button.component.scss'
})
export class FollowersButtonComponent implements OnInit, OnChanges {
  @Input() lead: any;

  isDropdownOpen = false;
  currentUser: any;
  currentLead: any;

  constructor(
    private userDataService: userDataService,
    private dialog: MatDialog,
    private userService: userServices
  ) {}

  async ngOnInit() {
    this.currentUser = await this.userDataService.getUserData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lead']) {
      this.currentLead = changes['lead'].currentValue;
    }
  }

  // UI helpers
  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  isCurrentUserFollowing(): boolean {
    if (!this.currentLead?.lead_subscriptions || !this.currentUser?.id) return false;
    return this.currentLead.lead_subscriptions.some(
      (f: any) => f?.user_id === this.currentUser.id || f?.user?.id === this.currentUser.id
    );
  }

  isCurrentUserOwnerOrSecondaryOwner(): boolean {
    if (!this.currentLead || !this.currentUser?.id) return false;
    return (
      this.currentLead?.user?.id === this.currentUser.id ||
      this.currentLead?.secondaryUser?.id === this.currentUser.id
    );
  }

  // ----- Shared guard logic (owner/secondary cannot unsubscribe) -----
  private async guardOwnerOrSecondary(userId: number): Promise<boolean> {
    // owner
    if (this.currentLead?.user?.id === userId) {
      const dialogTitle = 'Lead Unsubscription';
      const dialogMessage =
        this.currentUser?.id === userId
          ? 'You are the primary owner of this lead and cannot unsubscribe.'
          : 'This representative cannot be removed because they are the owner.';
      await this.callDialog(dialogTitle, dialogMessage, true, 'warning');
      return false;
    }

    // secondary owner
    if (this.currentLead?.secondaryUser?.id === userId) {
      const dialogTitle = 'Lead Unsubscription';
      const dialogMessage =
        this.currentUser?.id === userId
          ? 'You are the Secondary owner of this lead and cannot unsubscribe.'
          : 'This representative cannot be removed because they are the Secondary owner.';
      await this.callDialog(dialogTitle, dialogMessage, true, 'warning');
      return false;
    }

    return true;
  }

  // ----- Actions -----

  // Remove someone else from the list (admin/owner action via trash icon)
  async removeUser(data: any): Promise<void> {
    const targetUserId: number = data?.user_id ?? data?.user?.id;
    if (!targetUserId) return;

    // Guard owner/secondary
    const allowed = await this.guardOwnerOrSecondary(targetUserId);
    if (!allowed) return;

    const isConfirmed = await this.callDialog(
      'Unsubscribe Lead',
      'Are you sure you want to unsubscribe lead?'
    );

    if (isConfirmed) {
      await this.removeSubscription(data);
    }
  }

  // Current user starts following
  async followCurrentUser() {
    if (!this.currentLead?.id || !this.currentUser?.id) return;

    // avoid duplicate push if already following
    if (this.isCurrentUserFollowing()) return;

    const payload = {
      lead_id: this.currentLead.id,
      user_id: this.currentUser.id
    };

    try {
      const response = await firstValueFrom(this.userService.updateUserFollowers(payload));
      // Ensure we always push a normalized subscription shape
      // Expecting: { id, user_id, user: {...} }
      this.currentLead.lead_subscriptions = [
        ...(this.currentLead.lead_subscriptions ?? []),
        response
      ];
    } catch (e) {
      await this.callDialog('Subscription Error', 'Failed to start subscription.', true, 'warning');
    }
  }

  // Current user stops following — now with SAME owner/secondary guard as removeUser
  async unfollowCurrentUser() {
    if (!this.currentUser?.id || !this.currentLead?.lead_subscriptions) return;

    // Guard owner/secondary for the current user
    const allowed = await this.guardOwnerOrSecondary(this.currentUser.id);
    if (!allowed) return;

    const sub = this.currentLead.lead_subscriptions.find(
      (s: any) => s?.user_id === this.currentUser.id || s?.user?.id === this.currentUser.id
    );

    if (!sub) {
      await this.callDialog('Unsubscribe', 'Subscription not found.', true, 'info');
      return;
    }

    // Optional: ask confirmation like removeUser flow (comment out if not desired)
    const isConfirmed = await this.callDialog(
      'Unsubscribe Lead',
      'Are you sure you want to stop your subscription?'
    );
    if (!isConfirmed) return;

    await this.removeSubscription(sub);
  }

  addFollowers() {
    const currentUserLeadsCopy = [...(this.currentLead?.lead_subscriptions ?? [])];

    const dialogRef = this.dialog.open(ManageFollowersModalComponent, {
      width: '500px',
      data: {
        currentUserLeads: currentUserLeadsCopy,
        lead: this.currentLead
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.currentLead.lead_subscriptions = [...result];
      }
    });
  }

  // Server delete + local state update
  private async removeSubscription(data: any) {
    const subscriptionId: number = data?.id;
    if (!subscriptionId) return;

    try {
      await firstValueFrom(this.userService.removeUserFollowers(subscriptionId));

      // Prefer filtering by subscription id (robust regardless of embedded user object)
      this.currentLead.lead_subscriptions = (this.currentLead.lead_subscriptions ?? []).filter(
        (s: any) => s?.id !== subscriptionId
      );
    } catch (e) {
      await this.callDialog('Unsubscribe Error', 'Failed to unsubscribe.', true, 'warning');
    }
  }

  // Dialog wrapper
  private callDialog(header: string, content: string, isInfo = false, type = 'info') {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: { header, content, isInfo, type },
      width: '600px'
    });

    return dialogRef.afterClosed().toPromise();
  }
}
